import React, { useCallback, useMemo } from 'react'
import { Checkbox, Col } from 'antd'
import {
  filter,
  includes,
  intersection,
  isEmpty,
  isNil,
  map,
  size,
  sortBy,
  toString,
} from 'lodash'

import { GridFormItem } from '../../../core/components/sections'
import { getFilterOptions, isBooleanTrue } from '../../../core/utils'

import { Select } from '../../../common/components/sections'
import {
  StyledFullWidthSearchInput,
  StyledPaddingBottomRow,
} from '../../../common/components/styled'
import {
  ALL_SELECT_FILTER_ITEM,
  ALL_SELECT_OPTION_ITEM,
  EXCLUSION_STATUS,
  PAGINATION,
  PERMISSIONS,
  TASK_BACKLOG_FILTER,
  TASK_DUPLICATE_FILTER,
  UNASSIGNED_USER_FILTER_ITEM,
} from '../../../common/constants'
import { useUsersQuery } from '../../../common/hooks'

import { useLoggedInUser, useUserHasPermissions } from '../../../account/hooks'
import { checkIfQcAssignableUser } from '../../../account/utils'
import {
  DEFAULT_MENU_CURATION_TASK_LIST_FILTERS,
  MENU_AVAILABILITY_FILTER,
  MENU_CURATION_TASK_NO_ERROR_TAG_FILTER,
  MENU_CURATION_TASK_NO_TAG_FILTER,
  MENU_CURATION_TASK_STATUS,
  MENU_CURATION_TASK_TAGS,
} from '../../constants'
import { useMenuCurationTasksListFilters } from '../../hooks'

import MenuCurationTasksPriorityTaskProgressCounts from './menu-curation-tasks-priority-task-progress-counts'

const MenuCurationTasksFilters = () => {
  const {
    isCurrentUserAssigned,
    isCurrentUserAllocated,
    taskStatus,
    autoeatStatus,
    searchText,
    assignedTierOneCurator,
    assignedCurator,
    assignedQa,
    assignedQc,
    assignedScrapingReviewerUser,
    curatorId,
    qaId,
    duplicate,
    tags,
    menuAvailability,
    backlog,
    setFilters,
  } = useMenuCurationTasksListFilters()
  const user = useLoggedInUser()

  const { data: usersData } = useUsersQuery()

  const canViewBacklogFilter = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.VIEW_BACKLOG_FILTER,
  )
  const canViewAssignFilters = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.VIEW_ASSIGN_FILTERS,
  )

  const statusOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...getFilterOptions(MENU_CURATION_TASK_STATUS, 'label', 'value'),
    ],
    [],
  )

  const duplicateOptions = useMemo(
    () => [
      ALL_SELECT_OPTION_ITEM,
      ...getFilterOptions(TASK_DUPLICATE_FILTER, 'label', 'value'),
    ],
    [],
  )

  const backlogOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...getFilterOptions(TASK_BACKLOG_FILTER, 'label', 'value'),
    ],
    [],
  )

  const autoeatStatusOptions = useMemo(
    () => getFilterOptions(EXCLUSION_STATUS, 'label', 'value'),
    [],
  )

  const handleSetDuplicateValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        duplicate: value,
      })
    },
    [setFilters],
  )

  const handleSetBacklogValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        backlog: value,
      })
    },
    [setFilters],
  )

  const tagsOptions = useMemo(
    () => [
      MENU_CURATION_TASK_NO_ERROR_TAG_FILTER,
      MENU_CURATION_TASK_NO_TAG_FILTER,
      ...map(
        getFilterOptions(MENU_CURATION_TASK_TAGS, 'label', 'value'),
        option => ({ ...option, disabled: false }),
      ),
    ],
    [],
  )

  const menuAvailabilityFilterOptions = useMemo(
    () => [
      ALL_SELECT_OPTION_ITEM,
      ...getFilterOptions(MENU_AVAILABILITY_FILTER, 'label', 'value'),
    ],
    [],
  )

  const usersOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...sortBy(
        map(usersData, ({ id, name }) => ({ label: name, value: id })),
        ({ value }) => (value === user?.id ? -1 : 1),
      ),
    ],
    [user?.id, usersData],
  )

  const assignedFilterOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      UNASSIGNED_USER_FILTER_ITEM,
      ...sortBy(
        map(usersData, ({ id, name }) => ({ label: name, value: id })),
        ({ value }) => (value === user?.id ? -1 : 1),
      ),
    ],
    [user?.id, usersData],
  )

  const qcAssignableUsersOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      UNASSIGNED_USER_FILTER_ITEM,
      ...map(filter(usersData, checkIfQcAssignableUser), ({ id, name }) => ({
        label: name,
        value: id,
      })),
    ],
    [usersData],
  )

  const selectedAssignedScrapingReviewerUser = useMemo(
    () =>
      !isNil(assignedScrapingReviewerUser)
        ? toString(assignedScrapingReviewerUser)
        : null,
    [assignedScrapingReviewerUser],
  )

  const selectedCurator = useMemo(
    () => (!isNil(curatorId) ? toString(curatorId) : null),
    [curatorId],
  )

  const selectedQa = useMemo(
    () => (!isNil(qaId) ? toString(qaId) : null),
    [qaId],
  )

  const selectedAssignedTierOneCurator = useMemo(
    () =>
      !isNil(assignedTierOneCurator) ? toString(assignedTierOneCurator) : null,
    [assignedTierOneCurator],
  )

  const selectedAssignedCurator = useMemo(
    () => (!isNil(assignedCurator) ? toString(assignedCurator) : null),
    [assignedCurator],
  )

  const selectedAssignedQaId = useMemo(
    () => (!isNil(assignedQa) ? toString(assignedQa) : null),
    [assignedQa],
  )

  const selectedAssignedQcId = useMemo(
    () => (!isNil(assignedQc) ? toString(assignedQc) : null),
    [assignedQc],
  )

  const areMenuAvailabilityAndStatusDisabled = useMemo(
    () =>
      isBooleanTrue(isCurrentUserAssigned) ||
      isBooleanTrue(isCurrentUserAllocated),
    [isCurrentUserAllocated, isCurrentUserAssigned],
  )

  const handleSetAssignedScrapingUserValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        assignedScrapingReviewerUser: value,
      })
    },
    [setFilters],
  )

  const handleChangeCurator = useCallback(
    value => {
      setFilters({
        assignedCurator: null,
        assignedQa: null,
        assignedTierOneCurator: null,
        assignedQc: null,
        taskStatus: null,
        assignedUnknownWordsCurator: null,
        assignedUnknownWordsQa: null,
        currentPage: PAGINATION.DEFAULT_PAGE,
        curatorId: value,
      })
    },
    [setFilters],
  )

  const handleChangeQa = useCallback(
    value => {
      setFilters({
        assignedCurator: null,
        assignedQa: null,
        assignedTierOneCurator: null,
        assignedQc: null,
        assignedUnknownWordsCurator: null,
        assignedUnknownWordsQa: null,
        taskStatus: null,
        currentPage: PAGINATION.DEFAULT_PAGE,
        qaId: value,
      })
    },
    [setFilters],
  )

  const handleSetTagsValue = useCallback(
    value => {
      const isNoTagFilterBeingAdded = includes(
        value,
        MENU_CURATION_TASK_NO_TAG_FILTER.value,
      )
      const isNoTagFilterPresent =
        includes(tags, MENU_CURATION_TASK_NO_TAG_FILTER.value) &&
        size(tags) === 1
      if (isNoTagFilterBeingAdded && !isNoTagFilterPresent) {
        return setFilters({
          currentPage: PAGINATION.DEFAULT_PAGE,
          tags: [MENU_CURATION_TASK_NO_TAG_FILTER.value],
        })
      }

      const areOtherTagsBeingAdded = size(value) > 1
      if (
        isNoTagFilterPresent &&
        isNoTagFilterBeingAdded &&
        areOtherTagsBeingAdded
      ) {
        return setFilters({
          currentPage: PAGINATION.DEFAULT_PAGE,
          tags: filter(
            value,
            tag => tag !== MENU_CURATION_TASK_NO_TAG_FILTER.value,
          ),
        })
      }

      const isNoErrorTagFilterBeingAdded = includes(
        value,
        MENU_CURATION_TASK_NO_ERROR_TAG_FILTER.value,
      )
      const isNoErrorTagFilterPresent = includes(
        tags,
        MENU_CURATION_TASK_NO_ERROR_TAG_FILTER.value,
      )
      if (isNoErrorTagFilterBeingAdded && !isNoErrorTagFilterPresent) {
        return setFilters({
          currentPage: PAGINATION.DEFAULT_PAGE,
          tags: filter(
            value,
            tag =>
              !includes(
                [
                  MENU_CURATION_TASK_TAGS.HAS_TIMEOUT_ERROR.value,
                  MENU_CURATION_TASK_TAGS.HAS_UNCAUGHT_AI_ERROR.value,
                  MENU_CURATION_TASK_TAGS.HAS_UNCAUGHT_MENU_CURATOR_ERROR.value,
                ],
                tag,
              ),
          ),
        })
      }

      const areErrorTagsBeingAdded =
        !isEmpty(
          intersection(
            [
              MENU_CURATION_TASK_TAGS.HAS_TIMEOUT_ERROR.value,
              MENU_CURATION_TASK_TAGS.HAS_UNCAUGHT_AI_ERROR.value,
              MENU_CURATION_TASK_TAGS.HAS_UNCAUGHT_MENU_CURATOR_ERROR.value,
            ],
            value,
          ),
        ) && size(value) > 1

      if (
        isNoErrorTagFilterPresent &&
        isNoErrorTagFilterBeingAdded &&
        areErrorTagsBeingAdded
      ) {
        return setFilters({
          currentPage: PAGINATION.DEFAULT_PAGE,
          tags: filter(
            value,
            tag => tag !== MENU_CURATION_TASK_NO_ERROR_TAG_FILTER.value,
          ),
        })
      }

      return setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        tags: value,
      })
    },
    [setFilters, tags],
  )

  const handleSetSearchValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        searchText: value,
      })
    },
    [setFilters],
  )

  const handleSetAssignedTierOneCuratorValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        assignedTierOneCurator: value,
      })
    },
    [setFilters],
  )

  const handleSetAssignedCuratorValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        assignedCurator: value,
      })
    },
    [setFilters],
  )

  const handleSetAssignedQaValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        assignedQa: value,
      })
    },
    [setFilters],
  )

  const handleSetAssignedQcValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        assignedQc: value,
      })
    },
    [setFilters],
  )

  const handleSetTaskStatusValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        taskStatus: value,
        menuAvailability: includes(
          [
            MENU_CURATION_TASK_STATUS.TIER_ONE_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.UNKNOWN_WORDS_CURATION_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.UNKNOWN_WORDS_QA_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
          ],
          value,
        )
          ? ALL_SELECT_OPTION_ITEM.value
          : menuAvailability,
      })
    },
    [menuAvailability, setFilters],
  )

  const handleSetAutoeatStatusValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        autoeatStatus:
          value || DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.AUTOEAT_STATUS,
      })
    },
    [setFilters],
  )

  const handleChangeMenuAvailability = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        menuAvailability: isNil(value) ? ALL_SELECT_OPTION_ITEM.value : value,
        taskStatus: includes(
          [
            MENU_CURATION_TASK_STATUS.TIER_ONE_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.UNKNOWN_WORDS_CURATION_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.UNKNOWN_WORDS_QA_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
          ],
          taskStatus,
        )
          ? null
          : taskStatus,
      })
    },
    [setFilters, taskStatus],
  )

  const handleChangeIsCurrentUserAssigned = useCallback(
    ({ target: { checked } }) => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        isCurrentUserAssigned: checked,
        menuAvailability: ALL_SELECT_OPTION_ITEM.value,
        taskStatus: null,
      })
    },
    [setFilters],
  )

  const handleChangeIsCurrentUserAllocated = useCallback(
    ({ target: { checked } }) => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        isCurrentUserAllocated: checked,
        menuAvailability: ALL_SELECT_OPTION_ITEM.value,
        taskStatus: null,
      })
    },
    [setFilters],
  )

  return (
    <>
      <StyledPaddingBottomRow gutter={16}>
        <Col span={6}>
          <StyledFullWidthSearchInput
            placeholder="Search Brands"
            defaultValue={searchText}
            onSearch={handleSetSearchValue}
          />
        </Col>
      </StyledPaddingBottomRow>
      {canViewAssignFilters && (
        <StyledPaddingBottomRow gutter={16}>
          <GridFormItem span={4} label="Assigned Scraping Reviewer">
            <Select
              allowClear
              showSearch
              options={usersOptions}
              value={selectedAssignedScrapingReviewerUser}
              onChange={handleSetAssignedScrapingUserValue}
            />
          </GridFormItem>
          <GridFormItem span={4} label="Assigned Tier One Curator">
            <Select
              allowClear
              showSearch
              options={usersOptions}
              value={selectedAssignedTierOneCurator}
              onChange={handleSetAssignedTierOneCuratorValue}
            />
          </GridFormItem>
          <GridFormItem span={4} label="Assigned Curator">
            <Select
              allowClear
              showSearch
              options={assignedFilterOptions}
              value={selectedAssignedCurator}
              onChange={handleSetAssignedCuratorValue}
            />
          </GridFormItem>
          <GridFormItem span={4} label="Assigned Qa">
            <Select
              allowClear
              showSearch
              options={assignedFilterOptions}
              value={selectedAssignedQaId}
              onChange={handleSetAssignedQaValue}
            />
          </GridFormItem>
          <GridFormItem span={4} label="Assigned Qc">
            <Select
              allowClear
              showSearch
              options={qcAssignableUsersOptions}
              value={selectedAssignedQcId}
              onChange={handleSetAssignedQcValue}
            />
          </GridFormItem>
        </StyledPaddingBottomRow>
      )}
      <StyledPaddingBottomRow gutter={16}>
        <GridFormItem span={4} label="Assigned to me?">
          <Checkbox
            checked={isBooleanTrue(isCurrentUserAssigned)}
            onChange={handleChangeIsCurrentUserAssigned}
          />
        </GridFormItem>
        <GridFormItem span={4} label="Allocated to me?">
          <Checkbox
            checked={isBooleanTrue(isCurrentUserAllocated)}
            onChange={handleChangeIsCurrentUserAllocated}
          />
        </GridFormItem>
        <MenuCurationTasksPriorityTaskProgressCounts
          isDisabled={!isBooleanTrue(isCurrentUserAllocated)}
        />
      </StyledPaddingBottomRow>
      <StyledPaddingBottomRow gutter={16}>
        <GridFormItem span={4} label="Menu availability">
          <Select
            allowClear
            showSearch
            options={menuAvailabilityFilterOptions}
            value={menuAvailability}
            disabled={areMenuAvailabilityAndStatusDisabled}
            onChange={handleChangeMenuAvailability}
          />
        </GridFormItem>
        <GridFormItem span={4} label="Status">
          <Select
            allowClear
            showSearch
            disabled={areMenuAvailabilityAndStatusDisabled}
            options={statusOptions}
            value={taskStatus}
            onChange={handleSetTaskStatusValue}
          />
        </GridFormItem>
        <GridFormItem span={4} label="Curated by">
          <Select
            allowClear
            showSearch
            options={usersOptions}
            value={selectedCurator}
            onChange={handleChangeCurator}
          />
        </GridFormItem>
        <GridFormItem span={4} label="Qad by">
          <Select
            allowClear
            showSearch
            options={usersOptions}
            value={selectedQa}
            onChange={handleChangeQa}
          />
        </GridFormItem>
        <GridFormItem span={2} label="Autoeat Status">
          <Select
            allowClear
            showSearch
            options={autoeatStatusOptions}
            value={autoeatStatus}
            onChange={handleSetAutoeatStatusValue}
          />
        </GridFormItem>
        <GridFormItem span={2} label="Duplicate">
          <Select
            allowClear
            showSearch
            options={duplicateOptions}
            value={duplicate}
            onChange={handleSetDuplicateValue}
          />
        </GridFormItem>
        {canViewBacklogFilter && (
          <GridFormItem span={2} label="Backlog">
            <Select
              allowClear
              showSearch
              options={backlogOptions}
              value={backlog}
              onChange={handleSetBacklogValue}
            />
          </GridFormItem>
        )}
        <GridFormItem span={canViewBacklogFilter ? 2 : 4} label="Tags">
          <Select
            allowClear
            showSearch
            mode="multiple"
            options={tagsOptions}
            value={tags}
            placeholder="Select tags..."
            onChange={handleSetTagsValue}
          />
        </GridFormItem>
      </StyledPaddingBottomRow>
    </>
  )
}

export default MenuCurationTasksFilters
